// src/pages/copilot/CoPilot.jsx
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bot, Sparkles } from 'lucide-react';
import SessionSidebar from './components/SessionSidebar';
import ChatInput from './components/ChatInput';
import ChatMessage from './components/ChatMessage';
import { useCopilotSession } from './hooks/useCopilotSession';
import { useCopilotSessionList } from './hooks/useCopilotSessionList';
import { useAuth } from '../../context/AuthContext.jsx';
import '../Pages.css';

const BASE_SUGGESTIONS = [
  'Status of my active campaigns',
  'Which campaign is performing best?',
  'Which campaign is worst, and why?',
];
const PRIVILEGED_SUGGESTION = 'Compare team members';

export default function CoPilot() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sessions, create, remove } = useCopilotSessionList();
  const active = useCopilotSession(sessionId ? parseInt(sessionId, 10) : null);

  const isPrivileged = ['Owner', 'Admin'].includes(user?.workspace_role);
  const suggestions = isPrivileged ? [...BASE_SUGGESTIONS, PRIVILEGED_SUGGESTION] : BASE_SUGGESTIONS;

  useEffect(() => {
    if (sessionId) return;
    if (sessions.length > 0) {
      navigate(`/co-pilot/${sessions[0].id}`, { replace: true });
    }
  }, [sessionId, sessions, navigate]);

  const handleNew = async () => {
    const id = await create();
    navigate(`/co-pilot/${id}`);
  };

  const handleSuggestion = (text) => {
    if (active.streaming) return;
    active.send(text);
  };

  return (
    <div className="page copilot-page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <div className="copilot-title">
            <div className="ai-badge">
              <Bot size={20} />
              <Sparkles size={12} className="sparkle" />
            </div>
            <h2>Insights</h2>
          </div>
          <p>Ask questions about your campaigns and team performance.</p>
        </div>
      </div>
      <div className="copilot-container">
        <SessionSidebar
          sessions={sessions}
          activeId={sessionId ? parseInt(sessionId, 10) : null}
          onNew={handleNew}
          onSelect={(id) => navigate(`/co-pilot/${id}`)}
          onDelete={remove}
        />
        <div className="chat-panel">
          <div className="chat-messages">
            {(!sessionId || active.messages?.length === 0) && (
              <div className="copilot-empty">
                <div className="empty-hero-icon">
                  <Sparkles size={36} />
                </div>
                <h3>Ask Insights anything about your outreach.</h3>
                <p>Get plain-English answers about campaign performance, reply rates, and (for Owners/Admins) how team members compare.</p>

                {!sessionId && (
                  <div className="empty-cta">
                    <button className="primary-btn" onClick={handleNew}>
                      <Bot size={16} /> Start a new conversation
                    </button>
                  </div>
                )}

                {sessionId && (
                  <div className="empty-samples">
                    {suggestions.map((q) => (
                      <button key={q} className="sample" onClick={() => handleSuggestion(q)}>{q}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {sessionId && active.messages.map((m) => (
              <ChatMessage key={m.id} message={m} />
            ))}
            {sessionId && active.streaming && (
              <div className="chat-message assistant">
                <div className="message-avatar"><Bot size={20} /></div>
                <div className="message-content">
                  <div className="copilot-typing">
                    <span className="copilot-typing-label">Thinking…</span>
                    <span className="copilot-typing-dots">
                      <span></span><span></span><span></span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          {sessionId && (
            <>
              {active.messages?.length > 0 && (
                <div className="insights-suggestions">
                  {suggestions.map((q) => (
                    <button
                      key={q}
                      className="insights-suggestion-chip"
                      disabled={active.streaming}
                      onClick={() => handleSuggestion(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              <ChatInput
                disabled={active.streaming}
                onSend={active.send}
                placeholder="Ask about your campaigns…"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
